import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as EnabledIcon,
  Cancel as DisabledIcon,
} from '@mui/icons-material';
import { debugError } from '../../../utils/debugLog';

/**
 * PolicyAssignmentHistory Component
 *
 * Displays the change history for a policy assignment in a timeline format.
 */
const PolicyAssignmentHistory = ({
  open,
  onClose,
  assignment,
  loadHistory,
  templateName,
}) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && assignment) {
      loadHistoryData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, assignment]);

  const loadHistoryData = async () => {
    if (!assignment || !assignment.assignment_id) {
      setError('Assignment ID is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const historyData = await loadHistory(assignment.assignment_id);
      setHistory(historyData || []);
    } catch (err) {
      debugError('Error loading history:', err);
      setError(err.message || 'Failed to load assignment history');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = action => {
    switch (action?.toLowerCase()) {
      case 'created':
        return <AddIcon />;
      case 'updated':
        return <EditIcon />;
      case 'deleted':
        return <DeleteIcon />;
      case 'enabled':
        return <EnabledIcon />;
      case 'disabled':
        return <DisabledIcon />;
      default:
        return <EditIcon />;
    }
  };

  const getActionColor = action => {
    switch (action?.toLowerCase()) {
      case 'created':
        return 'success';
      case 'updated':
        return 'primary';
      case 'deleted':
        return 'error';
      case 'enabled':
        return 'success';
      case 'disabled':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = dateString => {
    if (!dateString) return 'Unknown date';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Assignment History</Typography>
        {assignment && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {assignment.policy_name || 'Policy'} - Template: {templateName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!loading && !error && history.length === 0 && (
          <Alert severity="info">
            No history available for this assignment.
          </Alert>
        )}

        {!loading && !error && history.length > 0 && (
          <Timeline>
            {history.map((entry, index) => (
              <TimelineItem key={index}>
                <TimelineSeparator>
                  <TimelineDot color={getActionColor(entry.action)}>
                    {getActionIcon(entry.action)}
                  </TimelineDot>
                  {index < history.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Chip
                        label={entry.action || 'Unknown'}
                        color={getActionColor(entry.action)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(entry.changed_at)}
                      </Typography>
                    </Box>

                    {entry.changed_by && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Changed by: {entry.changed_by}
                      </Typography>
                    )}

                    {entry.change_reason && (
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, fontStyle: 'italic' }}
                      >
                        Reason: {entry.change_reason}
                      </Typography>
                    )}

                    {entry.old_values &&
                      Object.keys(entry.old_values).length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Previous Values:
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              mt: 0.5,
                              p: 1,
                              bgcolor: 'background.default',
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              overflow: 'auto',
                            }}
                          >
                            {JSON.stringify(entry.old_values, null, 2)}
                          </Box>
                        </Box>
                      )}

                    {entry.new_values &&
                      Object.keys(entry.new_values).length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            New Values:
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              mt: 0.5,
                              p: 1,
                              bgcolor: 'background.default',
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              overflow: 'auto',
                            }}
                          >
                            {JSON.stringify(entry.new_values, null, 2)}
                          </Box>
                        </Box>
                      )}
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PolicyAssignmentHistory;
